import isArray from "lodash/isArray";
import isNull from "lodash/isNull";
import isObject from "lodash/isObject";
import isString from "lodash/isString";
import isUndefined from "lodash/isUndefined";
import { useMemo, useState } from "react";
import JsonView from "react18-json-view";
import SearchInput from "../inputs/SearchInput";
import Flx from "../layout/Flx";
import ToggleTabSwitcher from "../navigation/ToggleTabSwitcher";
import Txt from "../typography/Txt";

const JsonPreview = ({
  values,
  show = true,
  sortKeys = true,
  showCollapseControls = true,
  defaultCollapse = 1,
  sticky = true,
}) => {
  const [search, setSearch] = useState("");
  const [collapse, setCollapse] = useState(defaultCollapse);
  const filteredValues = useMemo(
    () => filterJson(values, search, sortKeys),
    [values, search, sortKeys],
  );

  // Convert collapse level to a function for react18-json-view
  // This shows levels 0 through (collapse - 1) expanded, and collapses depth >= collapse
  const jsonViewCollapsed = useMemo(() => {
    if (collapse === 0) return false; // Expand all
    return (field) => field.depth >= collapse; // Collapse at depth >= collapse
  }, [collapse]);

  const headerStyles = useMemo(() => {
    if (!sticky) return {};
    return {
      position: "sticky",
      top: -14,
      left: 0,
      zIndex: 1111,
      background: "#ffffff",
    };
  }, [sticky]);

  const collapseControlsStyles = useMemo(() => {
    return {
      ".MuiToggleButtonGroup-root  ": {
        ".MuiButtonBase-root": {
          margin: "0 !important",
        },
        button: {
          fontSize: "10px !important",
        },
      },
    };
  }, []);

  if (!show) return null;
  return (
    <Flx column gap={2} sx={{ ".json-view div": { margin: "3px 0" } }}>
      <Flx fw ac gap={4} className={"json-view--header"} sx={headerStyles}>
        <SearchInput
          value={search}
          onChange={setSearch}
          fullWidth
          sx={{ maxWidth: "320px" }}
        />

        {showCollapseControls && (
          <Flx ac gap={0.5} sx={collapseControlsStyles}>
            <Txt
              sx={{
                fontSize: "10px",
              }}
            >
              Collapse:
            </Txt>

            <ToggleTabSwitcher
              value={collapse}
              onChange={(next) => setCollapse(Number(next))}
              tabs={[0, 1, 2, 3, 4]}
            />
          </Flx>
        )}
      </Flx>
      <JsonView
        src={filteredValues}
        theme="atom"
        enableClipboard
        displaySize
        collapsed={jsonViewCollapsed}
      />
    </Flx>
  );
};

const sortObjectKeys = (obj) => {
  if (!isObject(obj) || isNull(obj)) {
    return obj;
  }

  if (isArray(obj)) {
    return obj.map(sortObjectKeys);
  }

  const sortedKeys = Object.keys(obj).sort((a, b) => a.localeCompare(b));
  const sortedObj = {};

  for (const key of sortedKeys) {
    sortedObj[key] = sortObjectKeys(obj[key]);
  }

  return sortedObj;
};

const filterJson = (obj, search, shouldSort = true) => {
  if (!search) return shouldSort ? sortObjectKeys(obj) : obj;

  const lowerSearch = search.toLowerCase();

  const recursiveFilter = (data) => {
    if (isObject(data) && !isNull(data)) {
      if (isArray(data)) {
        const filteredArray = data
          .map(recursiveFilter)
          .filter((item) => !isUndefined(item));
        return filteredArray.length ? filteredArray : undefined;
      } else {
        const entries = Object.entries(data)
          .map(([key, value]) => {
            const filteredValue = recursiveFilter(value);
            const keyMatches = key.toLowerCase().includes(lowerSearch);
            const valueMatches =
              isString(value) && value.toLowerCase().includes(lowerSearch);
            if (keyMatches || valueMatches || !isUndefined(filteredValue)) {
              return [key, !isUndefined(filteredValue) ? filteredValue : value];
            }
            return null;
          })
          .filter(Boolean);

        if (entries.length) {
          if (shouldSort) {
            entries.sort(([a], [b]) => a.localeCompare(b));
          }
          return Object.fromEntries(entries);
        }
        return undefined;
      }
    } else if (isString(data) && data.toLowerCase().includes(lowerSearch)) {
      return data;
    }
    return undefined;
  };

  return recursiveFilter(obj);
};
export default JsonPreview;
